import React, { useMemo, useState } from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './ToolsPage.module.css';

const SITE = process.env.REACT_APP_MARKETPLACE_ROOT_URL || 'https://www.bigloads.com';
const CANONICAL = `${SITE}/tools/trailer-payload-calculator`;
const TITLE = 'Trailer Payload Calculator: Max Cargo Weight | BigLoads';
const DESC =
  'Free trailer payload calculator. Enter your trailer’s GVWR and empty weight to find your maximum safe cargo capacity, plus the ideal tongue weight range for stable towing.';

const lbs = n => Math.round(n).toLocaleString() + ' lbs';

const FAQS = [
  {
    q: 'What is trailer payload capacity?',
    a: 'Payload capacity is the maximum weight of cargo you can load onto a trailer. It equals the trailer’s GVWR (gross vehicle weight rating, the most it can legally weigh loaded) minus the trailer’s own empty weight. Never load past this number.',
  },
  {
    q: 'Where do I find my trailer’s GVWR?',
    a: 'The GVWR is stamped on the trailer’s VIN plate or certification label, usually on the front left frame or A-frame tongue. The empty (curb) weight may also be listed, or you can weigh the trailer at a scale. Rental listings on BigLoads show the trailer specs so you know before you book.',
  },
  {
    q: 'What is the ideal tongue weight?',
    a: 'For a bumper-pull trailer, tongue weight should be about 10 to 15 percent of the total loaded trailer weight. For gooseneck and fifth-wheel trailers it runs higher, around 15 to 25 percent. Too little tongue weight causes dangerous sway; too much overloads your tow vehicle’s rear axle.',
  },
  {
    q: 'What happens if I overload a trailer?',
    a: 'Overloading past the GVWR strains the axles, tires, brakes and frame, dramatically increases stopping distance, and can cause blowouts or structural failure. It’s also illegal and can void insurance. Always stay within both the trailer’s payload and your tow vehicle’s towing capacity.',
  },
  {
    q: 'Does axle rating matter too?',
    a: 'Yes. Each axle has its own weight rating (GAWR). The combined axle ratings should support the loaded trailer, and the load should be balanced so no single axle is overloaded. Proper front-to-back balance also sets your tongue weight correctly.',
  },
];

const TrailerPayloadPageComponent = ({ scrollingDisabled }) => {
  const [gvwr, setGvwr] = useState(7000);
  const [emptyWeight, setEmptyWeight] = useState(2000);
  const [tonguePct, setTonguePct] = useState(12);

  const result = useMemo(() => {
    const payload = Math.max(0, Number(gvwr) - Number(emptyWeight));
    const loaded = Number(gvwr);
    const tongueLow = loaded * 0.1;
    const tongueHigh = loaded * 0.15;
    const tongueTarget = loaded * (Number(tonguePct) / 100);
    return { payload, tongueLow, tongueHigh, tongueTarget };
  }, [gvwr, emptyWeight, tonguePct]);

  const articleSchema = {
    '@type': 'Article',
    headline: 'Trailer Payload Calculator: Max Cargo Weight',
    description: DESC,
    author: { '@type': 'Organization', name: 'BigLoads' },
    publisher: { '@type': 'Organization', name: 'BigLoads' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': CANONICAL },
  };
  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Tools', item: `${SITE}/tools` },
      { '@type': 'ListItem', position: 3, name: 'Trailer Payload Calculator', item: CANONICAL },
    ],
  };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={TITLE}
      description={DESC}
      schema={[articleSchema, faqSchema, breadcrumbSchema]}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        <section className={css.hero}>
          <div className={css.wrap}>
            <div className={css.eyebrow}>Free Tool for the Haul</div>
            <h1 className={css.h1}>Trailer Payload Calculator</h1>
            <p className={css.lede}>
              Find your trailer’s maximum safe cargo weight and the right tongue weight for a
              stable, sway-free tow.
            </p>
          </div>
        </section>

        <section className={css.calcWrap}>
          <div className={css.wrap}>
            <div className={css.calc}>
              <h2 className={css.calcH}>How Much Can I Load?</h2>
              <p className={css.calcSub}>
                Enter your trailer’s GVWR and empty weight (both are on the VIN plate).
              </p>

              <div className={css.row2}>
                <div className={css.field}>
                  <label className={css.fieldLabel}>
                    Trailer GVWR <span className={css.fieldHint}>(max loaded weight, lbs)</span>
                  </label>
                  <input
                    className={css.input}
                    type="number"
                    inputMode="numeric"
                    value={gvwr}
                    onChange={e => setGvwr(e.target.value)}
                  />
                </div>
                <div className={css.field}>
                  <label className={css.fieldLabel}>
                    Trailer Empty Weight <span className={css.fieldHint}>(curb weight, lbs)</span>
                  </label>
                  <input
                    className={css.input}
                    type="number"
                    inputMode="numeric"
                    value={emptyWeight}
                    onChange={e => setEmptyWeight(e.target.value)}
                  />
                </div>
              </div>

              <div className={css.field}>
                <label className={css.fieldLabel}>
                  Target Tongue Weight <span className={css.fieldHint}>(% of loaded weight — aim 10-15%)</span>
                </label>
                <div className={css.sliderRow}>
                  <input
                    className={css.slider}
                    type="range"
                    min="8"
                    max="25"
                    step="1"
                    value={tonguePct}
                    onChange={e => setTonguePct(e.target.value)}
                  />
                  <span className={css.sliderVal}>{tonguePct}%</span>
                </div>
              </div>

              <div className={css.result}>
                <div className={`${css.verdict} ${css.verdictNeutral}`}>
                  <p className={css.verdictH}>{lbs(result.payload)} of cargo</p>
                  <p className={css.verdictP}>
                    That’s the most you can safely load on this trailer before you hit its GVWR.
                    Stay under it, and keep the load balanced.
                  </p>
                </div>

                <div className={css.statGrid}>
                  <div className={css.stat}>
                    <div className={css.statNum}>{lbs(result.payload)}</div>
                    <div className={css.statLabel}>Max cargo (payload)</div>
                  </div>
                  <div className={css.stat}>
                    <div className={css.statNum}>
                      {Math.round(result.tongueLow).toLocaleString()}
                      {'–'}
                      {Math.round(result.tongueHigh).toLocaleString()}
                    </div>
                    <div className={css.statLabel}>Ideal tongue wt (lbs)</div>
                  </div>
                  <div className={css.stat}>
                    <div className={css.statNum}>{lbs(result.tongueTarget)}</div>
                    <div className={css.statLabel}>At {tonguePct}% target</div>
                  </div>
                </div>

                <div className={css.ctaBand}>
                  <NamedLink className={css.btnOrange} name="SearchPage">
                    Find a Trailer to Rent
                  </NamedLink>
                </div>

                <p className={css.disclaimer}>
                  Your trailer’s real GVWR and empty weight are on its VIN / certification plate.
                  Payload equals GVWR minus empty weight. Always confirm axle ratings (GAWR) and your
                  tow vehicle’s towing capacity as well. This tool is for planning only.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className={css.body}>
          <div className={css.wrap}>
            <h2>What Trailer Payload Really Means</h2>
            <p>
              Payload is the weight of the stuff you put on the trailer. It’s a simple
              subtraction: take the trailer’s <strong>GVWR</strong> (the maximum it’s rated
              to weigh fully loaded) and subtract the trailer’s <strong>empty weight</strong>.
              What’s left is how much cargo you can carry. Load past that and you’re over the
              trailer’s rating, which is unsafe and illegal.
            </p>

            <h2>Why Tongue Weight Matters</h2>
            <p>
              Hitting your payload number isn’t enough on its own. Where the weight sits decides
              whether the trailer tracks straight or fishtails. For a bumper-pull, you want tongue
              weight in the 10 to 15 percent range of the total loaded weight. Load too much toward
              the back and tongue weight drops, which invites dangerous sway. Load too much up front
              and you overload your tow vehicle’s rear axle and hitch.
            </p>
            <p>
              The fix is balance: put about 60 percent of the cargo weight ahead of the axle, keep it
              low and centered, and strap it down. Our{' '}
              <NamedLink name="SingleBlogPage" params={{ title: 'how-to-load-a-trailer-safely' }}>
                trailer loading guide
              </NamedLink>{' '}
              covers the full method.
            </p>

            <h2>Payload vs. Towing Capacity</h2>
            <p>
              Two different limits, and you have to respect both. Payload is about the trailer:
              how much it can carry. Towing capacity is about your truck: how much it can pull. A
              trailer might be rated for a 5,000 lb payload, but if your loaded trailer exceeds your
              truck’s tow rating, you still can’t safely haul it. Check your truck with our{' '}
              <NamedLink name="TowingCapacityPage">towing capacity calculator</NamedLink> too.
            </p>

            <h2>Match the Trailer to Your Job</h2>
            <p>
              Once you know how much you need to haul, you can pick a trailer with enough payload and
              rent it locally.{' '}
              <NamedLink name="SearchPage">Browse trailer rentals on BigLoads</NamedLink> and check
              the specs before you book.
            </p>
          </div>
        </section>

        <section className={css.faq}>
          <div className={css.wrap}>
            <h2 className={css.faqH}>Payload Calculator FAQ</h2>
            {FAQS.map(f => (
              <div className={css.faqItem} key={f.q}>
                <p className={css.faqQ}>{f.q}</p>
                <p className={css.faqA}>{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={css.related}>
          <div className={css.wrap}>
            <div className={css.relatedGrid}>
              <NamedLink className={css.relatedCard} name="TowingCapacityPage">
                <div className={css.relatedTitle}>Towing Capacity Calculator</div>
                <div className={css.relatedBody}>Can your truck safely pull it?</div>
              </NamedLink>
              <NamedLink className={css.relatedCard} name="SavingsCalculatorPage">
                <div className={css.relatedTitle}>Owner Income Calculator</div>
                <div className={css.relatedBody}>What could your trailer earn on BigLoads?</div>
              </NamedLink>
              <NamedLink
                className={css.relatedCard}
                name="SingleBlogPage"
                params={{ title: 'how-to-load-a-trailer-safely' }}
              >
                <div className={css.relatedTitle}>How to Load a Trailer Safely</div>
                <div className={css.relatedBody}>Balance, straps, and the 60/40 rule.</div>
              </NamedLink>
            </div>
          </div>
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

TrailerPayloadPageComponent.propTypes = { scrollingDisabled: bool.isRequired };

const mapStateToProps = state => ({ scrollingDisabled: isScrollingDisabled(state) });

const TrailerPayloadPage = compose(connect(mapStateToProps))(TrailerPayloadPageComponent);

export default TrailerPayloadPage;
